import { useEffect } from "react";
import {
  getInvoicesTemplates,
  getInvoiceTemplate,
} from "../../../api/invoicesTemplates";
import { useSettingsStore } from "../../../stores/settings";
import { showToastError } from "../../../services/toast";
import useModals from "../../../hooks/useModals";
import DeleteModal from "../../ui/modals/DeleteModal";
import AddTemplate from "../modals/AddTemplate";
import upload from "../../../assets/icons/darkIcons/download.svg";

const Templates = () => {
  const { templates, setTemplates, updateSettings } = useSettingsStore();

  const { modals, openModals, openModal, closeModal } = useModals();

  useEffect(() => {
    getInvoicesTemplates()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setTemplates(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));
  }, [updateSettings]);

  const handleGetInvoiceTemplate = (id: number, label: string) => {
    getInvoiceTemplate(id)
      .then((res) => {
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `template_${label}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du template");
      });
  };

  return (
    <div className="flex flex-col gap-5">
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      {/* En-tête */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="min-w-0">
          <p className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
            Facturation
          </p>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
            Templates de facture
          </h2>
          <p className="mt-1 font-Nunito text-sm text-slate-500">
            Gérez vos modèles et mettez-les à jour en quelques clics.
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            openModal(
              "addTemplate",
              <AddTemplate
                closeModal={() => closeModal("addTemplate")}
                update={false}
              />,
            )
          }
          className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-xl bg-light-red px-4 py-2.5 font-Nunito text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 hover:shadow-md hover:shadow-light-red/20 focus-visible:ring-2 focus-visible:ring-light-red/40 focus-visible:outline-none"
        >
          Ajouter un template
          <svg aria-hidden="true" className="size-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m-7-7h14" />
          </svg>
        </button>
      </div>

      {/* Liste */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
          {templates.length > 0 ? (
            templates.map((template) => (
              <div
                key={template.value}
                className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="font-Nunito text-sm font-semibold text-slate-900">
                    {template.label}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    Modèle n°{template.value}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:outline-none"
                    onClick={() =>
                      handleGetInvoiceTemplate(template.value, template.label)
                    }
                    aria-label={`Télécharger ${template.label}`}
                    title="Télécharger"
                  >
                    <img src={upload} alt="" className="size-4 opacity-70" />
                  </button>
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 font-Nunito text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:outline-none"
                    onClick={() =>
                      openModal(
                        "addTemplate",
                        <AddTemplate
                          closeModal={() => closeModal("addTemplate")}
                          id={template.value}
                          update={true}
                        />,
                      )
                    }
                  >
                    Remplacer
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(
                        "deleteModal",
                        <DeleteModal
                          closeModal={() => closeModal("deleteModal")}
                          id={template.value}
                        />,
                      );
                    }}
                    className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-light-red px-3.5 py-2 font-Nunito text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 hover:shadow-md hover:shadow-light-red/20 focus-visible:ring-2 focus-visible:ring-light-red/40 focus-visible:outline-none"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <svg className="size-12 mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="font-Nunito text-sm font-medium text-slate-500">Aucun template disponible</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Templates;
